import React from "react";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import { NavDropdown } from "./NavDropdown";
import { AccountSymbol } from "@/helpers/svgs";
import { theme } from "@/theme";
import { NavData } from "./NavItem";

interface TabPanelProps {
  children?: React.ReactNode;
  id?: string;
  index: any;
  value: any;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`nav-tabpanel-${index}`}
      aria-labelledby={`nav-tab-${index}`}
      {...other}
    >
      {value === index && <Typography component="div">{children}</Typography>}
    </div>
  );
}

interface NavTabsProps {
  links: NavData[];
  rootSelector: string;
  tabSelector: string;
  panelSelector: string;
  ariaLabel: string;
  minWidth?: string;
  maxWidth?: string;
  minTabWidth: string;
  className?: string;
  noGap?: boolean;
  align?: "left" | "center" | "right";
}

export function NavTabs(props: NavTabsProps) {
  const [value, setValue] = React.useState(false as number | boolean);
  const handleChange = (
    event: React.ChangeEvent<{}>,
    newValue: number | boolean
  ) => {
    setValue(newValue);
  };
  const {
    links,
    rootSelector,
    tabSelector,
    panelSelector,
    ariaLabel,
    minTabWidth,
    minWidth,
    maxWidth,
    className,
    noGap,
    align = "center",
  } = props;
  const root = document.querySelector(`.${rootSelector}`);
  if (root != null) {
    root.addEventListener("mouseleave", () => setValue(false));
  }

  for (let i = 0; i < links.length; i++) {
    const tab = document.querySelector(`#${tabSelector}${i}`);
    if (tab != null) {
      tab.addEventListener("mouseenter", () => setValue(i));
    }
    const panel = document.querySelector(`#${panelSelector}${i}`);
    if (panel != null) {
      panel.addEventListener("mouseenter", () => setValue(i));
      panel.addEventListener("mouseleave", () => setValue(false));
    }
  }

  const getAlignment = () => {
    switch (align) {
      case "left": {
        return "flex-start";
      }
      case "center": {
        return "space-between";
      }
      case "right": {
        return "flex-end";
      }
    }
  };

  return (
    <div
      className={`${rootSelector} ${className ? className : ""}`}
      style={{
        minWidth: minWidth || "unset",
      }}
    >
      <AppBar
        position="static"
        component="div"
        style={{
          backgroundColor: "transparent",
          color: "hsl(var(--color-primary))",
          boxShadow: "none",
          height: "78px",
        }}
      >
        <Tabs
          indicatorColor="primary"
          textColor="inherit"
          variant="standard"
          value={value}
          onChange={handleChange}
          aria-label={ariaLabel}
          style={{
            height: "100%",
            maxWidth: maxWidth || "unset",
          }}
          sx={{
            ".MuiTabs-flexContainer": {
              justifyContent: getAlignment(),
              gap: noGap ? "0px" : "8px",
            },
          }}
        >
          {links.length > 0 &&
            links.map((link, index) => {
              if (link.type === "divider") {
                return null;
              } else {
                const isLink = link.type == "link" || link.type == "button";
                const hasSubmenu = link.type == "submenu";
                const isAccountLink = hasSubmenu && link.title == "Account";
                return (
                  <Tab
                    className="site-nav__link--main"
                    tabIndex={0}
                    href={isLink ? link.href : ""}
                    label={isAccountLink ? "" : link.title}
                    icon={isAccountLink ? <AccountSymbol /> : ""}
                    id={tabSelector + index}
                    aria-controls={panelSelector + index}
                    aria-expanded={value === index}
                    onMouseEnter={() => {
                      setValue(index);
                    }}
                    disableFocusRipple
                    disableRipple
                    style={{
                      minWidth: minTabWidth,
                      fontWeight: theme.typography.fontWeightMedium,
                      fontSize: "14px",
                      opacity: 1,
                      lineHeight: "17px",
                      letterSpacing: "1px",
                      padding: "12px",
                      height: "78px",
                      outline: "revert",
                    }}
                    color="primary"
                    key={link.title}
                  />
                );
              }
            })}
        </Tabs>
      </AppBar>
      {links.length > 0 &&
        links.map(
          (link, index) =>
            link.type == "submenu" && (
              <TabPanel
                value={value}
                index={index}
                id={panelSelector + index}
                key={link.title}
              >
                <NavDropdown link={link} />
              </TabPanel>
            )
        )}
    </div>
  );
}
